/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Sparkles, Target, Wand2, MessageSquare, PenLine, Briefcase,
  ArrowRight, Check, Palette, Type, LayoutGrid as LayoutIcon,
  TrendingUp, Lock, Zap,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { analytics } from '../utils/analytics';

/**
 * /features  —  Editorial Scrollytelling redesign (Option A, shipped 2026-04-30).
 *
 * Layout:
 *   • Sticky LEFT column on lg+ (kicker, dynamic headline, dynamic CTA, social-proof strip).
 *   • Scrolling RIGHT column with 6 full-bleed product modules — each one a working
 *     preview of an Ascend feature.
 *
 * Conversion mechanic:
 *   The CTA in the sticky-left column changes label + destination based on which
 *   right-column module is currently in view (IntersectionObserver). Visitors never
 *   need to scroll back to convert — the relevant offer follows them down the page.
 *
 * Instrumentation:
 *   • analytics.clickCTA('features_view_<id>', 'features_scrollytelling_section')
 *     fires once per module the FIRST time it crosses the viewport.
 *   • Each contextual CTA click fires its own scoped event so GA4 can show
 *     conversion-by-section.
 *
 * Mobile / a11y:
 *   • Below `lg` (1024px), sticky behaviour is dropped — the page becomes a vertical
 *     stack of (headline → preview → CTA) units.
 *   • prefers-reduced-motion disables Framer entrance animations, scroll snapping,
 *     and the AI Coach typing dots.
 */

// ============================================================================
// Module specifications — single source of truth for left-rail context + visuals.
// ============================================================================
const MODULES = [
  {
    id: 'sage',
    eyebrow: 'Sage AI Copilot',
    headline: 'Meet Sage — your always-on career copilot.',
    sub:
      'Most AI chatbots forget you the second you close the tab. Sage reads your latest Ascend resume as context — every answer is tailored to YOUR career, your role, your gaps. It\'s the difference between asking a stranger for advice and asking a mentor who already knows your story.',
    bullets: [
      'Auto-loads your active resume on every conversation — no copy-pasting',
      'Saves history across sessions so coaching compounds over time',
      'Ask anything: "What should I fix this week?" · "Rewrite my LinkedIn headline" · "How do I follow up on Tuesday\'s interview?"',
      'Asks the questions a $400/hr human coach would ask — included in your subscription',
    ],
    cta: { label: 'Try Sage free for 14 days', to: '/register?ref=features-sage', tone: 'primary' },
  },
  {
    id: 'studio',
    eyebrow: 'WYSIWYG Editor',
    headline: 'Polish to the pixel — without the fiddly part.',
    sub:
      'Twelve industry-tuned themes. Notion-style inline editing. Pixel-identical PDF and Word export. Every detail of your resume, controlled from one place.',
    bullets: [
      '12 themes (Tech, Executive, Creative, Medical, Legal, Government, …)',
      'Live preview matches the final PDF and Word export — exactly',
      'Autosave every 1.5 seconds. Never lose a word.',
    ],
    cta: { label: 'Try Resume Studio free', to: '/register?ref=features-studio', tone: 'primary' },
  },
  {
    id: 'ats',
    eyebrow: 'Free ATS Check',
    headline: 'Know exactly why recruiters never see your resume.',
    sub:
      'Paste your resume. Get a 0-100 ATS score against any job in 30 seconds. The free check shows your top fix; sign up free to unlock every gap and quick win.',
    bullets: [
      'Real ATS algorithms — not a keyword counter',
      'Score-aware fix list, prioritized by impact',
      'Free, no signup needed. 532+ scans this month.',
    ],
    cta: { label: 'Scan my resume free', to: '/free-ats-check', tone: 'primary' },
  },
  {
    id: 'rewriter',
    eyebrow: 'AI Resume Rewriter',
    headline: 'Bullet by bullet, AI rewrites you into the interview.',
    sub:
      'Weak bullet in. Quantified, recruiter-loved, ATS-friendly bullet out. STAR-method structure with metrics inferred from your role and industry.',
    bullets: [
      'STAR method baked in (Situation → Task → Action → Result)',
      'Metrics inferred contextually, never hallucinated',
      'Re-runs in seconds — try multiple phrasings, pick your favorite',
    ],
    cta: { label: 'Rewrite my bullets', to: '/register?ref=features-rewriter', tone: 'primary' },
  },
  {
    id: 'cover',
    eyebrow: 'AI Cover Letter Generator',
    headline: 'Tailored cover letter. 8 seconds. Done.',
    sub:
      'Paste a job description. Get a cover letter that quotes the role’s exact language, weaves in your matching strengths, and reads like you wrote it after a long thinking walk.',
    bullets: [
      'Pulls strengths directly from your active resume',
      'Tone slider: confident, warm, technical, executive',
      'Export to PDF or copy as plain text in one click',
    ],
    cta: { label: 'Draft a cover letter', to: '/register?ref=features-cover', tone: 'primary' },
  },
  {
    id: 'jobs',
    eyebrow: 'Job Match Score',
    headline: 'Stop applying. Start tailoring.',
    sub:
      'Live job listings, every card scored against your active resume. Browse only the jobs you can credibly compete for — and tailor your resume to each one in a click.',
    bullets: [
      'Live JSearch feed — real openings, hourly refresh',
      'Match % computed against the resume you’re actively editing',
      'One-click "Tailor this resume" generates a job-specific variant',
    ],
    cta: { label: 'Browse matched jobs', to: '/register?ref=features-jobs', tone: 'primary' },
  },
];

// ============================================================================
// Module previews — six bespoke visuals, all built in HTML/CSS, no external assets.
// ============================================================================

const FrameWrapper = ({ children, dataTestId }) => (
  <div
    className="relative w-full bg-white border border-[#e5e7eb] overflow-hidden"
    style={{ boxShadow: '0 1px 0 rgba(15,17,21,0.04), 0 24px 60px -30px rgba(0,47,167,0.28), 0 48px 80px -40px rgba(15,17,21,0.18)' }}
    data-testid={dataTestId}
  >
    {/* macOS chrome */}
    <div className="flex items-center gap-3 px-4 py-3 border-b border-[#e5e7eb] bg-[#fafafa]">
      <div className="flex gap-1.5">
        <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
        <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
        <span className="w-3 h-3 rounded-full bg-[#28c840]" />
      </div>
      <div className="flex-1 mx-2 px-3 py-1 bg-white border border-[#e5e7eb] text-xs text-[#6b7280] font-mono truncate">
        ascendforyou.net
      </div>
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#002fa7]">Ascend</div>
    </div>
    {children}
  </div>
);

const StudioPreview = () => (
  <FrameWrapper dataTestId="preview-studio">
    <div className="grid grid-cols-[160px_1fr] min-h-[420px]">
      <aside className="border-r border-[#e5e7eb] bg-[#fafafa] p-3 text-[11px]">
        <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-[0.14em] font-semibold text-[#6b7280]"><Palette size={10}/> Theme</div>
        <div className="grid grid-cols-5 gap-1.5 mb-4">
          {['#002fa7','#0f766e','#7c3aed','#ea580c','#0891b2','#be185d','#65a30d','#4338ca','#b45309','#0f1115'].map((c, i) => (
            <span key={c} className="w-5 h-5 rounded-full" style={{ background: c, boxShadow: i===0 ? '0 0 0 2px #002fa7, 0 0 0 4px #fff' : '0 0 0 1px #e5e7eb' }} />
          ))}
        </div>
        <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-[0.14em] font-semibold text-[#6b7280]"><Type size={10}/> Type</div>
        <div className="flex border border-[#e5e7eb] bg-white mb-4">
          {['Sans','Serif','Mono'].map((l, i) => (
            <div key={l} className={`flex-1 text-center py-1 text-[10px] font-semibold ${i===0?'bg-[#002fa7] text-white':'text-[#4b5563]'}`}>{l}</div>
          ))}
        </div>
        <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-[0.14em] font-semibold text-[#6b7280]"><LayoutIcon size={10}/> Layout</div>
        <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] bg-white border border-[#e5e7eb] font-semibold"><Check size={9} className="text-[#002fa7]"/> Letter</div>
      </aside>
      <div className="bg-[#f4f5f7] p-4 relative">
        <div className="bg-white max-w-[380px] mx-auto p-4 border border-[#e5e7eb]" style={{ fontFamily: '"Iowan Old Style", Palatino, Georgia, serif' }}>
          <div className="text-center mb-3 pb-2 border-b border-[#e5e7eb]">
            <div className="text-[16px] font-extrabold tracking-tight text-[#0f1115]">Jordan Chen</div>
            <div className="text-[9px] uppercase tracking-[0.16em] text-[#002fa7] font-semibold mt-1">Senior Product Designer</div>
          </div>
          <h3 className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#0f1115] mb-1.5 pb-1 border-b-2 border-[#002fa7] inline-block">Experience</h3>
          <div className="text-[10px]">
            <div className="font-bold text-[#0f1115]">Lead Product Designer · Stripe</div>
            <div className="text-[#6b7280] italic text-[8px]">2022 — Present</div>
            <ul className="mt-1 space-y-0.5 list-disc pl-4 text-[#374151] text-[9.5px]">
              <li>Redesigned checkout; lifted completion 62% → 81%.</li>
              <li>Built component library used by 120+ engineers.</li>
            </ul>
          </div>
        </div>
        <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-white border border-[#e5e7eb] px-2 py-1 text-[10px] font-semibold text-[#0f766e]">
          <span className="features-pulse-dot w-1.5 h-1.5 rounded-full bg-[#10b981]" /> Autosaved
        </div>
      </div>
    </div>
  </FrameWrapper>
);

const AtsPreview = () => (
  <FrameWrapper dataTestId="preview-ats">
    <div className="p-6 bg-[#f4f5f7] min-h-[420px]">
      <div className="bg-white border border-[#e5e7eb] p-5 mb-4">
        <div className="text-[10px] uppercase tracking-[0.16em] text-[#6b7280] font-semibold mb-1">Your ATS Score</div>
        <div className="flex items-baseline gap-3 mb-3">
          <div className="text-5xl font-extrabold text-[#0f1115] tabular-nums">62</div>
          <div className="text-[#6b7280] text-sm">/ 100</div>
          <div className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#f59e0b] text-[#0f1115] text-[11px] font-bold uppercase tracking-wider">Close — fixable gaps</div>
        </div>
        <div className="h-1.5 bg-[#e5e7eb] overflow-hidden mb-1">
          <div className="features-score-fill h-full bg-gradient-to-r from-[#f59e0b] to-[#002fa7]" style={{ width: '62%' }} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-[#e5e7eb] p-3">
          <div className="flex items-center gap-1.5 mb-2 text-[11px] font-bold text-[#0f1115]"><Target size={12} className="text-[#dc2626]"/> Critical Gap</div>
          <div className="text-[11px] text-[#374151] leading-relaxed">Missing keyword: <span className="font-mono bg-[#fef3c7] px-1">product-led growth</span></div>
          <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-white bg-[#0f1115] px-2 py-1"><Lock size={9}/> 3 more</div>
        </div>
        <div className="bg-white border border-[#e5e7eb] p-3">
          <div className="flex items-center gap-1.5 mb-2 text-[11px] font-bold text-[#0f1115]"><Zap size={12} className="text-[#002fa7]"/> Quick Win</div>
          <div className="text-[11px] text-[#374151] leading-relaxed">Add B2B SaaS to professional summary</div>
          <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-white bg-[#0f1115] px-2 py-1"><Lock size={9}/> 4 more</div>
        </div>
      </div>
    </div>
  </FrameWrapper>
);

const RewriterPreview = () => (
  <FrameWrapper dataTestId="preview-rewriter">
    <div className="p-6 bg-[#f4f5f7] min-h-[420px]">
      <div className="grid grid-cols-1 gap-4">
        <div className="bg-white border border-[#e5e7eb] p-4">
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#dc2626] font-bold mb-2">Before</div>
          <div className="text-sm text-[#4b5563] line-through decoration-[#dc2626]/40">
            Worked on the checkout team and helped improve things for users by making the page faster.
          </div>
        </div>
        <div className="flex justify-center"><ArrowRight size={20} className="text-[#002fa7]" /></div>
        <div className="bg-white border border-[#002fa7] p-4 relative">
          <div className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.16em] text-[#002fa7] font-bold inline-flex items-center gap-1"><Sparkles size={11}/> AI Rewrite</div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#10b981] font-bold mb-2">After</div>
          <div className="text-sm text-[#0f1115] leading-relaxed">
            Led checkout-page performance initiative; reduced p95 load time from <strong>4.2s to 1.1s</strong>, lifting completion rate <strong>62% → 81%</strong> across 1.2M monthly sessions.
          </div>
        </div>
      </div>
    </div>
  </FrameWrapper>
);

const SagePreview = () => (
  <FrameWrapper dataTestId="preview-sage">
    <div className="p-5 bg-[#f4f5f7] min-h-[420px] flex flex-col gap-3">
      <div className="text-xs text-[#6b7280] flex items-center gap-2 mb-1">
        <Sparkles size={12} className="text-[#002fa7]" />
        Sage has loaded: <span className="font-mono text-[#002fa7]">Senior Engineer Resume v3.docx</span>
      </div>
      <div className="self-end max-w-[80%] bg-[#002fa7] text-white px-4 py-2.5 text-sm">
        I'm pivoting from backend to platform eng. Should I lead my resume with my Kubernetes work or my system design?
      </div>
      <div className="self-start max-w-[85%] bg-white border border-[#e5e7eb] px-4 py-3 text-sm text-[#374151] leading-relaxed">
        <div className="flex items-center gap-1.5 mb-1.5 text-[10px] uppercase tracking-[0.14em] font-bold text-[#002fa7]">
          <Sparkles size={10} /> Sage
        </div>
        Lead with system design. Looking at your resume, the Stripe checkout work shows clearer staff-level scope (cross-team coordination, 1.2M sessions) than your K8s migration. Move <span className="font-mono text-[#002fa7]">"Reduced API p95 4.2s → 1.1s"</span> to the top bullet of your most recent role. That single line earns you the platform-eng interview.
      </div>
      <div className="self-end max-w-[60%] bg-[#002fa7] text-white px-4 py-2.5 text-sm">
        What should I fix this week?
      </div>
      <div className="self-start inline-flex items-center gap-1 text-xs text-[#6b7280]">
        <span className="features-pulse-dot w-1.5 h-1.5 rounded-full bg-[#10b981]" />
        Sage is typing…
      </div>
    </div>
  </FrameWrapper>
);

const CoverLetterPreview = () => (
  <FrameWrapper dataTestId="preview-cover">
    <div className="p-6 bg-[#f4f5f7] min-h-[420px]">
      <div className="bg-white border border-[#e5e7eb] p-5" style={{ fontFamily: 'Georgia, serif' }}>
        <div className="text-[11px] text-[#6b7280] mb-3">Dear Vercel Hiring Team,</div>
        <p className="text-[12.5px] text-[#374151] leading-[1.7]">
          Your <span className="bg-[#fef3c7] px-1">Staff Platform Engineer</span> posting calls out <em>improving build reliability for 80,000+ projects</em> — that's exactly the work I led at Stripe, where I rebuilt our deployment pipeline and brought build success rate from 91.4% to 99.7% over four months…
        </p>
        <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[#002fa7]">
          <Sparkles size={11}/> 8 seconds · Tone: confident
        </div>
      </div>
    </div>
  </FrameWrapper>
);

const JobsPreview = () => (
  <FrameWrapper dataTestId="preview-jobs">
    <div className="p-5 bg-[#f4f5f7] min-h-[420px] space-y-2.5">
      {[
        { role: 'Staff Platform Engineer', co: 'Vercel', loc: 'Remote · US', match: 94, color: '#10b981' },
        { role: 'Senior Backend Engineer', co: 'Stripe', loc: 'San Francisco', match: 88, color: '#10b981' },
        { role: 'Engineering Manager — Infra', co: 'Linear', loc: 'Remote · Worldwide', match: 71, color: '#f59e0b' },
        { role: 'Lead Frontend Engineer', co: 'Notion', loc: 'NYC · Hybrid', match: 52, color: '#dc2626' },
      ].map((job) => (
        <div key={job.role} className="bg-white border border-[#e5e7eb] p-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-bold text-[#0f1115] truncate">{job.role}</div>
            <div className="text-[11px] text-[#6b7280] truncate">{job.co} · {job.loc}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#6b7280] font-semibold leading-tight">Match</div>
            <div className="text-2xl font-extrabold tabular-nums leading-none" style={{ color: job.color }}>{job.match}</div>
          </div>
        </div>
      ))}
    </div>
  </FrameWrapper>
);

const PREVIEW_BY_ID = {
  sage: SagePreview,
  studio: StudioPreview,
  ats: AtsPreview,
  rewriter: RewriterPreview,
  cover: CoverLetterPreview,
  jobs: JobsPreview,
};

// ============================================================================
// Page
// ============================================================================
export default function FeaturesPage() {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(MODULES[0]);
  const seenRef = useRef(new Set());

  // Track per-module impressions for GA4 attribution.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const sections = MODULES.map((m) => document.getElementById(`features-module-${m.id}`)).filter(Boolean);
    const io = new IntersectionObserver(
      (entries) => {
        // Pick the visible entry that comes EARLIEST in the page (smallest
        // boundingClientRect.top among visible entries). Previously this
        // sorted by intersectionRatio, which on initial load caused the
        // rail to show module-1 (Studio) instead of module-0 (Sage) when
        // both were partially visible. Document order is the more
        // intuitive behavior for a top-down scrollytelling page.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          const id = visible[0].target.id.replace('features-module-', '');
          const m = MODULES.find((x) => x.id === id);
          if (m) setActive(m);
          if (!seenRef.current.has(id)) {
            seenRef.current.add(id);
            try { analytics.clickCTA(`features_view_${id}`, 'features_scrollytelling_section'); } catch (_) {}
          }
        }
      },
      { threshold: [0, 0.4, 0.75], rootMargin: '-25% 0px -25% 0px' },
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <SEOHead
        title="Features — Ascend"
        description="Resume Studio, AI rewriter, free ATS check, Sage AI Copilot, cover letters, job match — every Ascend feature, in one scrollable story."
        canonical="https://ascendforyou.net/features"
      />
      <div className="bg-[#f4f5f7] min-h-screen" data-testid="features-page">

        {/* INTRO */}
        <section className="border-b border-[#e5e7eb] bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-4">All Features</div>
            <h1 className="font-satoshi font-black text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight text-[#0f1115] max-w-4xl">
              Your career,<br /><span className="text-[#002fa7]">engineered.</span>
            </h1>
            <p className="mt-6 text-lg text-[#4b5563] max-w-2xl leading-relaxed">
              Six features that turn job applications into interviews. Scroll through the live product on the right — the offer on the left changes to match.
            </p>
          </div>
        </section>

        {/* MAIN — sticky-left + scrolling-right */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 py-8 lg:py-16">

            {/* STICKY LEFT */}
            <aside className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start lg:max-h-[calc(100vh-10rem)]">
              <motion.div
                key={active.id}
                initial={reduce ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
              >
                <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-3" data-testid="features-rail-eyebrow">{active.eyebrow}</div>
                <h2 className="font-satoshi font-bold text-3xl sm:text-4xl lg:text-[2.5rem] leading-[1.05] tracking-tight text-[#0f1115] mb-5" data-testid="features-rail-headline">
                  {active.headline}
                </h2>
                <p className="text-[#4b5563] text-base lg:text-lg leading-relaxed mb-6">{active.sub}</p>
                <ul className="space-y-2.5 mb-8">
                  {active.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[#374151]">
                      <Check size={18} className="text-[#002fa7] shrink-0 mt-0.5" />
                      <span className="leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap items-center gap-4">
                  <Link
                    to={active.cta.to}
                    onClick={() => analytics.clickCTA(`features_cta_${active.id}`, 'features_scrollytelling_rail')}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="features-rail-cta"
                  >
                    <span>{active.cta.label}</span>
                    <ArrowRight size={18} />
                  </Link>
                  <Link
                    to="/pricing"
                    onClick={() => analytics.clickCTA('features_secondary_pricing', 'features_scrollytelling_rail')}
                    className="text-sm font-semibold text-[#0f1115] underline decoration-[#002fa7] decoration-2 underline-offset-4 hover:decoration-4 transition-all"
                  >
                    See pricing
                  </Link>
                </div>

                {/* Module pagination dots */}
                <div className="hidden lg:flex items-center gap-2 mt-10" role="tablist" aria-label="Feature sections">
                  {MODULES.map((m) => (
                    <a
                      key={m.id}
                      href={`#features-module-${m.id}`}
                      className={`block h-1 transition-all ${m.id === active.id ? 'w-10 bg-[#002fa7]' : 'w-5 bg-[#e5e7eb] hover:bg-[#9ca3af]'}`}
                      title={m.eyebrow}
                      aria-label={`Jump to ${m.eyebrow}`}
                    />
                  ))}
                </div>

                {/* Trust strip */}
                <div className="mt-8 pt-6 border-t border-[#e5e7eb] grid grid-cols-3 gap-4 text-xs text-[#6b7280]">
                  <div><div className="text-base font-bold text-[#0f1115]">+15</div>avg ATS score lift</div>
                  <div><div className="text-base font-bold text-[#0f1115]">30s</div>to first scan</div>
                  <div><div className="text-base font-bold text-[#0f1115]"><TrendingUp size={14} className="inline mr-0.5"/>2×</div>interview rate</div>
                </div>
              </motion.div>
            </aside>

            {/* SCROLLING RIGHT */}
            <div className="lg:col-span-7 space-y-24 lg:space-y-40 pb-24">
              {MODULES.map((m, idx) => {
                const Preview = PREVIEW_BY_ID[m.id];
                return (
                  <section
                    key={m.id}
                    id={`features-module-${m.id}`}
                    data-testid={`features-module-${m.id}`}
                    className="scroll-mt-32"
                  >
                    {/* Mobile-only headline echoes the rail content for sticky-less devices */}
                    <div className="lg:hidden mb-5">
                      <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-2">{m.eyebrow}</div>
                      <h3 className="font-satoshi font-bold text-2xl text-[#0f1115] tracking-tight">{m.headline}</h3>
                    </div>
                    <motion.div
                      initial={reduce ? false : { opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      viewport={{ once: true, amount: 0.2 }}
                      aria-hidden="true"
                    >
                      <Preview />
                    </motion.div>
                    {/* Mobile-only CTA below preview (sticky rail is hidden) */}
                    <div className="lg:hidden mt-5">
                      <Link
                        to={m.cta.to}
                        onClick={() => analytics.clickCTA(`features_cta_${m.id}_mobile`, 'features_scrollytelling_inline')}
                        className="btn-primary inline-flex items-center gap-2"
                        data-testid={`features-mobile-cta-${m.id}`}
                      >
                        {m.cta.label} <ArrowRight size={16} />
                      </Link>
                    </div>
                    <div className="mt-6 hidden lg:flex items-center gap-2 text-xs text-[#6b7280]">
                      <span className="font-mono tabular-nums">{String(idx + 1).padStart(2, '0')} / {String(MODULES.length).padStart(2, '0')}</span>
                      <span className="h-px flex-1 bg-[#e5e7eb]" />
                      <span className="uppercase tracking-[0.16em] font-semibold">{m.eyebrow}</span>
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
        </section>

        {/* CLOSING CTA — full-bleed dark anchor */}
        <section className="bg-[#0f1115] text-white py-20 lg:py-28">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#fde68a] mb-4">Founding Member sale active</div>
            <h2 className="font-satoshi font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] mb-5">
              Try every feature free. Upgrade when you&rsquo;re ready.
            </h2>
            <p className="text-white/75 text-lg mb-8 max-w-2xl mx-auto">
              No credit card. Free ATS check, free Resume Studio, free first resume. 40% off Yearly Pro auto-applied at checkout — no code needed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register?ref=features-bottom"
                onClick={() => analytics.clickCTA('features_bottom_signup', 'features_closing')}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-[#0f1115] font-bold hover:bg-white/90 transition-colors"
                data-testid="features-closing-cta"
              >
                <Sparkles size={18}/> Start free
                <ArrowRight size={18}/>
              </Link>
              <Link to="/free-ats-check"
                onClick={() => analytics.clickCTA('features_bottom_ats', 'features_closing')}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors"
              >
                Just scan my resume first
              </Link>
            </div>
          </div>
        </section>

        {/* Page-scoped CSS — pulse animation for autosave + ATS bar fill */}
        <style>{`
          @keyframes featuresPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.45); }
            50%      { box-shadow: 0 0 0 5px rgba(16,185,129,0); }
          }
          .features-pulse-dot { animation: featuresPulse 2.4s ease-in-out infinite; }
          @keyframes featuresFill {
            from { width: 0; }
            to   { width: 62%; }
          }
          .features-score-fill { animation: featuresFill 1.4s cubic-bezier(0.22,0.61,0.36,1) both; }
          @media (prefers-reduced-motion: reduce) {
            .features-pulse-dot, .features-score-fill { animation: none; }
          }
        `}</style>
      </div>
    </>
  );
}
